import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { Utterance } from "../types/utterance";
import type { Transcriber, TranscriptionState } from "./Transcriber";
import { processIntakeFile } from "../api/intakeProcessing";

export class FileTranscriber implements Transcriber {
  private state: TranscriptionState = "idle";
  private stopFn: null | (() => Promise<void>) = null;
  private utteranceSubs = new Set<(u: Utterance) => void>();
  private stateSubs = new Set<(s: TranscriptionState) => void>();
  private errorSubs = new Set<(e: Error) => void>();
  private file: File;

  constructor(file: File) {
    this.file = file;
  }

  getState() {
    return this.state;
  }

  private setState(s: TranscriptionState) {
    this.state = s;
    this.stateSubs.forEach((fn) => fn(s));
  }

  onUtterance(cb: (u: Utterance) => void) {
    this.utteranceSubs.add(cb);
    return () => this.utteranceSubs.delete(cb);
  }

  onState(cb: (s: TranscriptionState) => void) {
    this.stateSubs.add(cb);
    return () => this.stateSubs.delete(cb);
  }

  onError(cb: (e: Error) => void) {
    this.errorSubs.add(cb);
    return () => this.errorSubs.delete(cb);
  }

  async start(opts: { languages: string[] }) {
    const startOverall = Date.now();
    if (this.stopFn) return; // already running
    this.setState("connecting");
    console.log(`[FileTranscriber] Starting batch processing for file: ${this.file.name} (${this.file.size} bytes)`);
    
    try {
      // Use processIntakeFile to get the transcribed narrative
      const result = await processIntakeFile(this.file);
      console.log(`[FileTranscriber] Batch processing completed in ${Date.now() - startOverall}ms`);
      
      if (result.narrative) {
        // Treat the narrative as a single utterance
        const utterance: Utterance = {
          speaker: 1,
          text: result.narrative,
          lang: opts.languages[0] || "en-US",
          timestamp: new Date().toISOString(),
          start: 0,
          end: 0,
        };
        
        console.log(`[FileTranscriber] Emitting narrative as utterance (${result.narrative.length} chars)`);
        this.utteranceSubs.forEach((fn) => fn(utterance));
        
        // Signal completion
        setTimeout(() => {
          this.setState("stopped");
        }, 100);
      } else {
        throw new Error("No narrative returned from processIntakeFile");
      }
    } catch (e: any) {
      console.error(`[FileTranscriber] Batch processing failed after ${Date.now() - startOverall}ms:`, e);
      this.setState("error");
      this.errorSubs.forEach((fn) => fn(e));
      throw e;
    }
  }

  async stop() {
    try {
      await this.stopFn?.();
    } finally {
      this.stopFn = null;
      this.setState("stopped");
    }
  }
}
